import { useEffect, useCallback, useRef } from "react";
import {
  useCortexContext,
  type CortexContextHandler,
} from "@via/context/cortex-context";
import { connectSocket } from "@via/utils/socket";
import type { ReactNode } from "react";

interface UseRegisterCortexContextOptions {
  routePattern: string; // e.g., "/runs/:runId", "/projects/:projectId"
  contextName: string;
  // Function that returns fresh context data when called
  // Use 'content' key for text/HTML content that should be sent to AI
  // Use 'filePath' + 'runUUID' keys for file-based context
  getContext: () => Record<string, any>;
  systemPrompt?: string;
  customContent?: ReactNode;
  enabled?: boolean;
}

/**
 * Simplified hook to register a Cortex context handler for any page.
 *
 * @example
 * ```tsx
 * // File-based context (automatically uploads to LLM)
 * useRegisterCortexContext({
 *   routePattern: "/runs/:runId",
 *   contextName: "Report Viewer",
 *   getContext: () => ({
 *     filePath: report?.routePath,
 *     runUUID,
 *     reportName: report?.name,
 *   }),
 *   systemPrompt: "You are analyzing a report file...",
 * });
 *
 * // Text-based context (sends text directly to LLM)
 * useRegisterCortexContext({
 *   routePattern: "/runs/:runId",
 *   contextName: "Run Logs",
 *   getContext: () => ({
 *     runId,
 *     content: logsContentString,
 *     logsData: data,
 *   }),
 *   systemPrompt: "You are analyzing pipeline run logs...",
 * });
 * ```
 */
export function useRegisterCortexContext({
  routePattern,
  contextName,
  getContext,
  systemPrompt,
  customContent,
  enabled = true,
}: UseRegisterCortexContextOptions) {
  const { registerContextHandler, unregisterContextHandler } =
    useCortexContext();

  // Keep the latest getContext in a ref so the registered handler
  // always sees fresh data without forcing re-registration on every render.
  const getContextRef = useRef(getContext);

  useEffect(() => {
    getContextRef.current = getContext;
  }, [getContext]);

  // Wrap getContext to automatically inject current URL
  const getContextWithUrl = useCallback(() => {
    const contextData = getContextRef.current();
    return {
      ...contextData,
      // Automatically add current page path (relative URL) to every context
      pageUrl:
        typeof window !== "undefined"
          ? window.location.pathname +
            window.location.search +
            window.location.hash
          : "",
    };
  }, []);

  // Proactively establish socket connection when hook mounts
  // This ensures the connection is ready before the user asks a question
  useEffect(() => {
    if (!enabled) return;

    const socket = connectSocket("/file-summarize");

    // If not connected, start connecting (but don't wait - it will connect in background)
    if (socket.disconnected) {
      socket.connect();
    }

    // Cleanup: connection pooling handles cleanup, so we don't need to disconnect
    // The socket will be reused across hook instances
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      unregisterContextHandler(routePattern, contextName);
      return;
    }

    // Register the handler with simplified structure
    const handler: CortexContextHandler = {
      routePattern,
      contextName,
      getContext: getContextWithUrl, // Use wrapped function with URL
      systemPrompt,
      customContent: customContent as JSX.Element | undefined,
    };

    registerContextHandler(handler);

    return () => {
      unregisterContextHandler(routePattern, contextName);
    };
  }, [
    routePattern,
    contextName,
    systemPrompt,
    customContent,
    enabled,
    registerContextHandler,
    unregisterContextHandler,
  ]);
}
